import { Injectable, inject } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventType, InteractionStatus } from '@azure/msal-browser';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
} from 'rxjs';
import {
  AuthenticatedIdentity,
  AuthenticationService,
  LogoutOption,
  identityKey,
} from '@ups/security';
import { ENTRA_LOGIN_CONFIG } from '../dual-login/dual-login.ui-service';

/**
 * MSAL implementation of {@link AuthenticationService}, lifted from the
 * per-app copies in ryns-roost / ryn-control-center and generalized:
 * scopes come from {@link ENTRA_LOGIN_CONFIG} (the app environment's
 * `entraId` block) instead of hard-coded values. Emits the signed-in
 * account's UPN as an `email` identity so `SecurityService` resolves
 * permissions via the by-email path on the Software Security API (the
 * same lookup `EntraIdSecurityUserLookupMiddleware` uses server-side).
 *
 * Only instantiate in apps where `buildEntraMsalProviders` registered
 * MSAL (it injects MsalService non-optionally by design — constructing
 * it in an unconfigured app is a wiring bug, not a runtime state).
 */
@Injectable({ providedIn: 'root' })
export class EntraAuthenticationService implements AuthenticationService {
  private readonly msal = inject(MsalService);
  private readonly broadcast = inject(MsalBroadcastService);
  private readonly entraConfig = inject(ENTRA_LOGIN_CONFIG);

  public readonly isAuthenticated$ = new ReplaySubject<boolean>(1);
  public readonly initialized$ = new ReplaySubject<boolean>(1);

  private readonly current$ = new BehaviorSubject<AuthenticatedIdentity | null>(
    null
  );
  readonly identity$: Observable<AuthenticatedIdentity | null> = this.current$
    .asObservable()
    .pipe(distinctUntilChanged((a, b) => identityKey(a) === identityKey(b)));

  readonly claims$ = new BehaviorSubject<Record<string, unknown> | null>(null);

  constructor() {
    this.initialized$.next(false);

    this.broadcast.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        map(() => this.identityFromMsal())
      )
      .subscribe((identity) => this.current$.next(identity));

    this.broadcast.msalSubject$
      .pipe(
        filter(
          (msg) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.ACTIVE_ACCOUNT_CHANGED ||
            msg.eventType === EventType.LOGOUT_SUCCESS
        )
      )
      .subscribe(() => {
        this.current$.next(this.identityFromMsal());
        this.claims$.next(
          this.msal.instance.getActiveAccount()?.idTokenClaims ?? null
        );
      });

    // Session state settles WITH MSAL interactions, not at construction
    // (#29713). On the boot returning from the Microsoft redirect this
    // service constructs BEFORE handleRedirectObservable caches the
    // account, so a one-shot read here reported false forever and a
    // freshly signed-in user was stranded on the login page. inProgress$
    // reaches None only after redirect processing completes (and is a
    // replayed current-value for late constructions), so reading there is
    // race-free in both orders. A cached account without an active-account
    // marker is promoted — acquireTokenSilent resolves the ACTIVE account,
    // and leaving it unset turned every API call into no_account_error.
    // The initial identity publish is gated behind initialize() (WI 31092):
    // this constructor runs at module construction, msal-browser v5 throws
    // uninitialized_public_client_application on pre-init account reads, and
    // the persisted localStorage cache means RETURNING boots hold accounts,
    // exactly the population the old eager read would crash (grok N2, PR 2744
    // thread 33372; same defect the Roost copy had). The broadcast
    // subscriptions above are safe where they are: they only read the
    // instance when events fire, which is post-init by construction.
    this.msal.initialize().subscribe(() => {
      this.initialized$.next(true);
      this.current$.next(this.identityFromMsal());
    });
    this.broadcast.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        map(() => {
          const instance = this.msal.instance;
          const accounts = instance.getAllAccounts();
          if (!instance.getActiveAccount() && accounts.length > 0) {
            instance.setActiveAccount(accounts[0]);
          }
          // Claims settle here too (#30536). The msalSubject$ handler above
          // only feeds claims$ from login/token events observed AFTER this
          // service constructs — a refresh boot with a cached session fires
          // no such event at all, and on the redirect-return boot
          // LOGIN_SUCCESS fires inside the APP_INITIALIZER before this
          // lazily-constructed service subscribes. Either way claims$
          // stayed null, UserEffects saw no UPN to resolve the security
          // user id from, and it warned + stayed null for the session.
          // The active account's cached idTokenClaims are the same values
          // the event handler would have published.
          this.claims$.next(instance.getActiveAccount()?.idTokenClaims ?? null);
          return Boolean(instance.getActiveAccount());
        })
      )
      .subscribe(this.isAuthenticated$);
  }

  deferInitialized<T>(callback: () => Observable<T>): Observable<T> {
    return this.initialized$.pipe(
      filter((x) => x),
      take(1),
      switchMap(() => callback())
    );
  }

  logout(options: LogoutOption): void {
    this.deferInitialized(() =>
      this.msal.logoutRedirect({ postLogoutRedirectUri: options.returnTo })
    ).subscribe();
  }

  getAccessTokenSilently(forceRefresh = false): Observable<string> {
    const scopes = this.entraConfig?.audience
      ? [`${this.entraConfig.audience}/.default`]
      : [];
    // forceRefresh bypasses MSAL's access-token cache and renews via the
    // refresh token (#31749). A page left open past the token lifetime 401s;
    // the interceptor's 401-recovery replays with a forced acquire so the
    // retry carries a genuinely new token instead of the cached one that just
    // 401'd. Default false keeps the cache-first boot/normal path unchanged.
    // Wait for MSAL to be ready before asking for a token (#30531).
    // acquireTokenSilent called before initialize() completes throws
    // BrowserAuthError uninitialized_public_client_application, which the
    // interceptor treats as a refresh failure — the request then proceeds
    // unauthenticated and the boot-time permission load 401s on EVERY
    // boot. inProgress$ reaching InteractionStatus.None is the only
    // signal that BOTH initialize AND handleRedirectObservable finished,
    // and it replays its current value to late subscribers, so gating on
    // it is race-free in both orders (same reasoning as #29713 in the
    // constructor).
    return this.broadcast.inProgress$.pipe(
      filter((status) => status === InteractionStatus.None),
      take(1),
      switchMap(() => this.msal.acquireTokenSilent({ scopes, forceRefresh })),
      map((result) => result.accessToken)
    );
  }

  private identityFromMsal(): AuthenticatedIdentity | null {
    const accounts: AccountInfo[] = this.msal.instance.getAllAccounts();
    const account =
      this.msal.instance.getActiveAccount() ?? accounts[0] ?? null;
    if (!account) return null;
    // MSAL surfaces the UPN on `username` for Azure AD sign-ins. Fall back
    // to idTokenClaims.preferred_username for B2C/guest edge cases.
    const upn =
      account.username ||
      (account.idTokenClaims as { preferred_username?: string } | undefined)
        ?.preferred_username;
    if (!upn) return null;
    return { kind: 'email', email: upn };
  }
}
